/**
 * 入力画面の上半分（Issue #467）。**表示だけ**で、盤のタップは受けない（押せるのは下端の表示の切り替えだけ）。
 * フィールド動画を world（動画のピクセル）に置き、選んだ表示の矩形を箱に contain で収める（stage.fitTransform。はみ出さない）。
 * 同じ world の座標で、ゾーンの枠・core の盤面のカード（boardSprites）・候補の印・選んでいるカードの印を重ねる。
 * 起きたイベントと発話のテキストは上半分には出さない（下半分の InfoStrip。動画と盤面を文字で隠さない）。
 * `live`（liveVideo.js）を渡すと `<video>` の代わりにその canvas を置き、videoProps をそのイベントに繋ぐ（収録中のライブ映像。Issue #617）。
 */
import { useEffect, useRef, useState } from 'react';
import { playerColor, usePlayerColors } from './common';
import { boardSprites, fitTransform, viewRect, visibleZones, VIEWS } from './stage';

const CYAN = '#4fc3ff';
const GOLD = '#ffd36b';

function useBoxSize(ref) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setBox((prev) => {
      const next = { w: el.clientWidth, h: el.clientHeight };
      return prev && prev.w === next.w && prev.h === next.h ? prev : next;
    });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [ref]);
  return box;
}

function Sprite({ s, scale, image, back, gray, mark, focus, color }) {
  const { rect } = s;
  const rot = (s.owner === 1 ? 180 : 0) + (s.defense ? 90 : 0);
  const line = (px) => `${px / Math.max(scale, 0.01)}px`;
  const src = s.faceUp ? image : back;
  const radius = rect.w * 0.05;
  return (
    <div data-testid="stage-card" data-key={s.key ?? ''} data-mark={mark ? '1' : undefined} data-focus={focus ? '1' : undefined}
      style={{ position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h, transform: rot ? `rotate(${rot}deg)` : undefined, borderRadius: radius }}>
      {src ? <img alt="" src={src} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: radius, opacity: 0.92 }} />
        : <div style={{ width: '100%', height: '100%', borderRadius: radius, boxSizing: 'border-box', background: s.faceUp ? '#2a3552' : '#141a2e', border: `${line(1)} solid ${color}` }} />}
      {gray ? <div style={{ position: 'absolute', inset: 0, borderRadius: radius, background: 'rgba(123,129,144,0.82)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: rect.w * 0.5 }}>?</div> : null}
      {s.count > 1 ? <div style={{ position: 'absolute', right: 0, bottom: 0, background: 'rgba(5,6,12,0.85)', color: '#fff', fontSize: rect.w * 0.3, lineHeight: 1.1, padding: `0 ${rect.w * 0.05}px`, borderRadius: radius }}>{s.count}</div> : null}
      {mark || focus ? <div style={{ position: 'absolute', inset: `-${line(focus ? 4 : 3)}`, border: `${line(focus ? 4 : 2.5)} solid ${focus ? GOLD : CYAN}`, borderRadius: rect.w * 0.08, boxShadow: focus ? `0 0 ${line(10)} ${GOLD}` : undefined }} /> : null}
    </div>
  );
}

/** `<video>` の props（onLoadedMetadata など）を、同じ顔をしたライブ映像（liveVideo.js）のイベントに繋ぐ */
const LIVE_EVENTS = { onLoadedMetadata: 'loadedmetadata', onLoadedData: 'loadeddata', onTimeUpdate: 'timeupdate', onPlay: 'play', onPause: 'pause', onSeeked: 'seeked' };

function LiveSurface({ live, onVideoRef, videoProps, videoSize }) {
  const hostRef = useRef(null);
  const propsRef = useRef(videoProps); propsRef.current = videoProps;
  useEffect(() => {
    const host = hostRef.current;
    const canvas = live.drawSource;
    if (host && canvas) {
      Object.assign(canvas.style, { position: 'absolute', left: '0', top: '0', width: '100%', height: '100%', display: 'block' });
      canvas.dataset.testid = 'input-live';
      host.appendChild(canvas);
    }
    const off = Object.entries(LIVE_EVENTS).map(([prop, type]) => {
      const fn = () => propsRef.current?.[prop]?.({ currentTarget: live });
      live.addEventListener(type, fn);
      return () => live.removeEventListener(type, fn);
    });
    onVideoRef?.(live);
    return () => { off.forEach((f) => f()); if (canvas?.parentNode === host) host.removeChild(canvas); onVideoRef?.(null); };
  }, [live]);   // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={hostRef} style={{ position: 'absolute', left: 0, top: 0, width: videoSize?.w ?? '100%', height: videoSize?.h ?? '100%' }} />;
}

/** upright: 見え方ごと回しているとき（P2 の席など）、札だけ回し戻して正立で読めるように */
function StatPlate({ s, upright = false }) {
  const { zone, stats } = s;
  const fs = zone.w * 0.2;
  return (
    <div data-testid="stage-stats" data-key={s.key ?? ''} style={{ position: 'absolute', left: zone.x, top: zone.y + zone.h - fs * (stats.level > 0 ? 2.5 : 1.3), width: zone.w,
      transform: upright ? 'rotate(180deg)' : undefined,
      background: 'rgba(0,0,0,0.72)', borderRadius: fs * 0.25, padding: `${fs * 0.08}px 0`, textAlign: 'center', lineHeight: 1.15, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
      {stats.level > 0 ? <div style={{ fontSize: fs * 0.95, color: '#f0c040' }}>★{stats.level}</div> : null}
      <div style={{ fontSize: fs, whiteSpace: 'nowrap' }}><span style={{ color: '#ff9a9a' }}>{stats.atk ?? '?'}</span><span style={{ color: '#aaa' }}>/</span><span style={{ color: '#9ab8ff' }}>{stats.def ?? '?'}</span></div>
    </div>
  );
}

export default function StageView({
  movieUrl, live = null, onVideoRef, videoProps = {}, videoSize, zones, board, images = {}, backs = [null, null], gray = new Set(),
  marks = new Set(), focus = null, zoneMarks = new Set(), view = 'all', views = ['all'], onViewChange, showBoard = true, onToggleBoard,
  status = null, life = [8000, 8000], players = [], flip = false,
}) {
  const colors = usePlayerColors();
  const pc = (p) => playerColor(p, colors) ?? colors[0];
  const boxRef = useRef(null);
  const box = useBoxSize(boxRef);
  // flip: P2 の席は全体・フィールドを 180° 回して自分の側を下に（Issue #717。呼ぶ側が seatView.flipsView で決める）
  const baseRect = viewRect(view, videoSize) ?? viewRect('all', videoSize);
  const rect = baseRect && flip ? { ...baseRect, rotate: (baseRect.rotate ?? 0) + 180 } : baseRect;
  const fit = fitTransform(rect, box);
  const scale = fit?.scale ?? 1;
  const sprites = showBoard ? boardSprites(board, zones, videoSize) : [];
  const line = (px) => `${px / Math.max(scale, 0.01)}px`;
  const chip = (on) => ({ padding: '3px 9px', minHeight: 26, borderRadius: 999, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
    border: `1px solid ${on ? CYAN : '#345'}`, background: on ? '#1b3a55' : 'rgba(15,21,38,0.85)', color: '#e8eefc' });
  const name = (p) => players?.[p]?.name ?? `P${p + 1}`;
  const side = (p) => ({ color: pc(p), fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
  return (
    <div data-testid="input-stage" style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', background: '#05060c', minHeight: 0 }}>
      {/* 状況の帯: LP・ターン・フェイズ */}
      <div data-testid="stage-status" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: '#e8eefc', flexShrink: 0 }}>
        <span style={side(flip ? 1 : 0)}>{flip ? `${name(1)} ${life[1]}` : `${name(0)} ${life[0]}`}</span>
        <span style={{ margin: '0 auto', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          {status?.turn > 0 ? <><span style={{ width: 10, height: 10, borderRadius: 999, background: pc(status.turnPlayer) }} />T{status.turn} {status.phaseText ?? ''}</> : '—'}
        </span>
        <span style={side(flip ? 0 : 1)}>{flip ? `${life[0]} ${name(0)}` : `${life[1]} ${name(1)}`}</span>
      </div>
      <div ref={boxRef} style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div data-testid="stage-world" style={{ position: 'absolute', left: 0, top: 0, width: videoSize?.w ?? 1, height: videoSize?.h ?? 1, transformOrigin: '0 0', transform: fit?.transform, pointerEvents: 'none', overflow: 'hidden', visibility: fit ? 'visible' : 'hidden' }}>
          {live ? <LiveSurface live={live} onVideoRef={onVideoRef} videoProps={videoProps} videoSize={videoSize} /> : null}
          {movieUrl && !live ? <video ref={onVideoRef} src={movieUrl} crossOrigin="anonymous" playsInline preload="auto" data-testid="input-video"
            style={{ position: 'absolute', left: 0, top: 0, width: videoSize?.w ?? '100%', height: videoSize?.h ?? '100%', display: 'block' }} {...videoProps} /> : null}
          {showBoard ? Object.entries(visibleZones(zones, videoSize)).map(([key, r]) => {
            const owner = Number(key.split(':')[1]);
            const on = zoneMarks.has(key);
            return <div key={key} data-zone={key} data-mark={on ? '1' : undefined} style={{ position: 'absolute', left: r.x, top: r.y, width: r.w, height: r.h, boxSizing: 'border-box',
              border: `${line(on ? 3 : 1)} solid ${on ? CYAN : pc(owner)}`, background: on ? 'rgba(79,195,255,0.28)' : 'transparent', opacity: on ? 1 : 0.55 }} />;
          }) : null}
          {sprites.map((s, i) => (
            <Sprite key={`${s.key}:${i}`} s={s} scale={scale} image={s.code ? images[s.code] : null} back={backs[s.owner] ?? null} color={pc(s.owner)}
              gray={!!s.key && gray.has(s.key)} mark={!!s.key && marks.has(s.key)} focus={!!s.key && s.key === focus} />
          ))}
          {/* ATK / DEF / レベル（表側のモンスター。カードの回転に付いていかないよう、ゾーンの下端に正立で重ねる） */}
          {sprites.filter((s) => s.stats && s.zone).map((s) => <StatPlate key={`stat:${s.key}`} s={s} upright={!!rect?.rotate} />)}
        </div>
        {!movieUrl && !live ? <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8896b3', fontSize: 13 }}>フィールド動画がありません</div> : null}
        {/* 表示の切り替え（上半分から出ない範囲で拡大） */}
        <div data-testid="stage-views" style={{ position: 'absolute', left: 6, right: 6, bottom: 6, display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
          {VIEWS.filter((v) => views.includes(v.key)).map((v) => (
            <button key={v.key} type="button" aria-pressed={view === v.key} style={chip(view === v.key)} onClick={() => onViewChange?.(v.key)}>{v.label}</button>
          ))}
          {onToggleBoard ? <button type="button" aria-pressed={showBoard} style={chip(showBoard)} onClick={onToggleBoard}>盤面</button> : null}
        </div>
      </div>
    </div>
  );
}
